/**
 * The client book's cross-sell indicator (prototype `xsell(c)`), with the opportunities query's state carried
 * through. "None" is a claim — the client has no open opportunity — so it is shown only once GET /opportunities
 * has answered. Until then the cell is a neutral placeholder; if the request failed it is "—", never "None".
 *
 * GET /opportunities and GET /clients take the same capabilities (clients.read.all / clients.read.own), so anyone
 * who can open the book can load this: an error is a real failure, not a permission the role lacks.
 */
export type CrossSellStatus = 'loading' | 'error' | 'ready';

/** Data on hand wins: a failed background refetch keeps showing the last good counts. */
export function crossSellStatus(q: { data?: unknown; isError: boolean }): CrossSellStatus {
  if (q.data !== undefined) return 'ready';
  return q.isError ? 'error' : 'loading';
}

/** Table cell: "N in progress" (chip hot), else "None" — only when the counts are known. */
export function CrossSellCell({ n, status }: { n: number; status: CrossSellStatus }) {
  if (status === 'loading')
    return (
      <span className="sub" aria-busy="true" title="Loading cross-sell…">
        …
      </span>
    );
  if (status === 'error')
    return (
      <span className="sub" title="Couldn't load cross-sell">
        —
      </span>
    );
  return n ? <span className="chip hot">{n} in progress</span> : <span className="sub">None</span>;
}

/** Box chip: "N cross-sell" after the priority chip. Absence on a box makes no claim, so nothing shows unless known. */
export function CrossSellChip({ n, status }: { n: number; status: CrossSellStatus }) {
  if (status !== 'ready' || !n) return null;
  return (
    <span className="chip hot" title="Cross-sell work in progress">
      {n} cross-sell
    </span>
  );
}
